"use client";

import { mergeProps } from "@base-ui/react/merge-props";
import { useRender } from "@base-ui/react/use-render";
import * as React from "react";

import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

function composeEventHandlers<E extends { defaultPrevented: boolean }>(
  consumerHandler: ((event: E) => void) | undefined,
  internalHandler: (event: E) => void,
) {
  return (event: E) => {
    consumerHandler?.(event);
    if (!event.defaultPrevented) internalHandler(event);
  };
}

export type InputBaseContextProps = Pick<
  InputBaseProps,
  "autoFocus" | "disabled"
> & {
  controlRef: React.RefObject<HTMLElement | null>;
  onFocusedChange: (focused: boolean) => void;
};

const InputBaseContext = React.createContext<InputBaseContextProps>({
  autoFocus: false,
  controlRef: { current: null },
  disabled: false,
  onFocusedChange: () => {},
});

function useInputBase() {
  const context = React.useContext(InputBaseContext);
  if (!context) {
    throw new Error("useInputBase must be used within a <InputBase />.");
  }

  return context;
}

export interface InputBaseProps extends React.ComponentProps<"div"> {
  autoFocus?: boolean;
  disabled?: boolean;
  error?: boolean;
}

type InputBaseAdornmentRenderProps = React.ComponentProps<"div"> & {
  "data-slot"?: string;
};

function InputBase({
  autoFocus,
  disabled,
  className,
  onClick,
  error,
  ...props
}: InputBaseProps) {
  const [focused, setFocused] = React.useState(false);
  const controlRef = React.useRef<HTMLElement>(null);

  return (
    <InputBaseContext.Provider
      value={{
        autoFocus,
        controlRef,
        disabled,
        onFocusedChange: setFocused,
      }}
    >
      <div
        data-slot="input-base"
        // Based on MUI's <InputBase /> implementation.
        // https://github.com/mui/material-ui/blob/master/packages/mui-material/src/InputBase/InputBase.js#L458~L460
        onClick={composeEventHandlers(onClick, (event) => {
          if (controlRef.current && event.currentTarget === event.target) {
            controlRef.current.focus();
          }
        })}
        className={cn(
          "interactive has-disabled:bg-muted has-disabled:text-muted-foreground flex min-h-9 cursor-text items-center gap-2 rounded-lg border px-3 py-1 text-sm outline-hidden focus-within:outline-hidden has-disabled:cursor-not-allowed",
          disabled && "pointer-events-none cursor-not-allowed opacity-50",
          focused && "ring-ring border-transparent ring-2",
          error &&
            "ring-destructive/20 dark:ring-destructive/40 border-destructive",
          className,
        )}
        {...props}
      />
    </InputBaseContext.Provider>
  );
}

function InputBaseFlexWrapper({
  className,
  ...props
}: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="input-base-flex-wrapper"
      className={cn("flex flex-1 flex-wrap", className)}
      {...props}
    />
  );
}

function InputBaseControl({
  ref,
  children,
  onFocus,
  onBlur,
  ...props
}: Omit<React.ComponentProps<"span">, "children"> & {
  children: React.ReactElement;
}) {
  const { controlRef, autoFocus, disabled, onFocusedChange } = useInputBase();
  const controlProps = {
    "data-slot": "input-base-control",
    autoFocus,
    disabled,
    onFocus: composeEventHandlers(onFocus, () => onFocusedChange(true)),
    onBlur: composeEventHandlers(onBlur, () => onFocusedChange(false)),
  };

  return useRender({
    defaultTagName: "span",
    render: children,
    ref: [controlRef, ref ?? null],
    props: mergeProps(controlProps, props),
  });
}

export type InputBaseAdornmentProps = useRender.ComponentProps<"div">;

function InputBaseAdornment({
  className,
  render,
  ...props
}: InputBaseAdornmentProps) {
  const adornmentProps: InputBaseAdornmentRenderProps = {
    "data-slot": "input-base-adornment",
    className: cn(
      "text-muted-foreground flex items-center [&_svg:not([class*='size-'])]:size-4",
      "[&:not(:has(button))]:pointer-events-none",
      className,
    ),
  };
  return useRender({
    defaultTagName: "div",
    render,
    props: mergeProps<"div">(adornmentProps, props),
  });
}

function InputBaseAdornmentButton({
  type = "button",
  variant = "ghost",
  size = "icon",
  disabled: disabledProp,
  className,
  ...props
}: React.ComponentProps<typeof Button>) {
  const { disabled } = useInputBase();

  return (
    <Button
      data-slot="input-base-adornment-button"
      type={type}
      variant={variant}
      size={size}
      disabled={disabled || disabledProp}
      className={cn("size-6", className)}
      {...props}
    />
  );
}

function InputBaseInput({
  className,
  ...props
}: React.ComponentProps<"input">) {
  return (
    <input
      data-slot="input-base-input"
      className={cn(
        "placeholder:text-placeholder file:text-foreground w-full flex-1 bg-transparent leading-none file:border-0 file:bg-transparent file:text-sm file:font-medium focus:outline-hidden disabled:pointer-events-none",
        className,
      )}
      {...props}
    />
  );
}

function InputBaseTextarea({
  className,
  ...props
}: React.ComponentProps<"textarea">) {
  return (
    <textarea
      data-slot="input-base-textarea"
      className={cn(
        "placeholder:text-placeholder min-h-16 flex-1 bg-transparent focus:outline-hidden disabled:pointer-events-none",
        className,
      )}
      {...props}
    />
  );
}

export {
  InputBase,
  InputBaseFlexWrapper,
  InputBaseControl,
  InputBaseAdornment,
  InputBaseAdornmentButton,
  InputBaseInput,
  InputBaseTextarea,
  useInputBase,
};
